import React, { forwardRef, useMemo, useState, useCallback } from 'react'
import { FieldTypes, urlRegexValidator } from 'utils/formUtils';
import WizMappingForm from './WizMappingForm';
import GenericPullCredentialsForm from '../GenericPullCredentialsForm';
import { Divider, Form, Tooltip } from "antd";
import _ from "lodash";
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { InfoIcon } from 'components/Icons';

const TYPE = {
    apiEndpoint: 'apiEndpoint',
    wizBucket: 'wizBucket', 
}

const wizRBVMAssetTypes = [
    {
      label: "Virtual Machine",
      value: "VIRTUAL_MACHINE"
    },
    {
      label: "Virtual Machine Image",
      value: "VIRTUAL_MACHINE_IMAGE"
    },
    {
      label: "Container Image",
      value: "CONTAINER_IMAGE"
    },
    {
        label: "Container",
        value: "CONTAINER"
    },
    {
        label: "Serverless",
        value: "SERVERLESS"
    },
    {
        label: "Kubernetes Cluster",
        value: "KUBERNETES_CLUSTER"
    },
    {
        label: "Kubernetes Node",
        value: "KUBERNETES_NODE"
    },
    {
        label: "Artifact Registry",
        value: "CONTAINER_REGISTRY"
    },
    {
        label: "Artifact Repository",
        value: "CONTAINER_REPOSITORY"
    },
    {
        label: "Access Role",
        value: "ACCESS_ROLE"
    },
    {
        label: "API Gateway",
        value: "API_GATEWAY"
    },
    {
        label: "Bucket",
        value: "BUCKET"
    },
    {
        label: "Database",
        value: "DATABASE"
    },
    {
      "label": "Subnet",
      "value": "SUBNET"
    },
    {
      "label": "Firewall",
      "value": "FIREWALL"
    },
    {
      "label": "Gateway",
      "value": "GATEWAY"
    },
    {
      "label": "Load Balancer",
      "value": "LOAD_BALANCER"
    },
    {
      "label": "Virtual Network",
      "value": "VIRTUAL_NETWORK"
    },
    {
      "label": "Private Endpoint",
      "value": "PRIVATE_ENDPOINT"
    },
    {
      "label": "Route Table",
      "value": "ROUTE_TABLE"
    },
    {
      "label": "CDN",
      "value": "CDN"
    },
    {
      "label": "Cloud Log Configuration",
      "value": "CLOUD_LOG_CONFIGURATION"
    },
    {
      "label": "DNS Record",
      "value": "DNS_RECORD"
    },
    {
      "label": "Network Interface",
      "value": "NETWORK_INTERFACE"
    }
];

function WizCredentialsForm(props, ref) {
    const [form] = Form.useForm();
    const featureFlags = useFeatureFlag();
    const [type, setType] = useState(() => _.isEmpty(props.config?.bucketName) ? TYPE.apiEndpoint : TYPE.wizBucket);

    const handleRadioChange = useCallback((e) => {
        setType(e.target.value);
    },[]);

    const [projects, setProjects] = useState(() => {
        if (props.config?.assetProjects && props.config?.assetProjectNames) {
            const { assetProjects, assetProjectNames } = props.config;
            const projectIdArray = assetProjects.split(',');
            const projectNameArray = assetProjectNames.split(',');
            return projectIdArray.map((id, index) => ({id, name: projectNameArray[index]}))
        }
        return [];
    });
    const [fieldValues, setFieldValues] = useState(() => {
        if(props.config){
            const formFieldValues = props.config;
            return {apiEndpoint: formFieldValues.apiEndpoint, clientId: formFieldValues.clientId, clientSecret: formFieldValues.clientSecret, fetchAssets: formFieldValues.fetchAssets === "true" || formFieldValues.fetchAssets === true, };
        }
        return {apiEndpoint: undefined, clientId: undefined, clientSecret: undefined, fetchAssets: false};
    });
    const [isApiEndpointValid, setIsApiEndpointValid] = useState(() => {
        return !_.isEmpty(props.config?.apiEndpoint);
    });
    
    const setOptionsFromPagination = useCallback((option) => {
        setProjects((prev) => _.uniqBy([...prev, ...option], "id"));
    }, []);
    
    const fetchProjects = useCallback((params) => {
        if (!fieldValues) return;
        const { apiEndpoint, clientId, clientSecret, cloudType } = fieldValues;
        
        if (apiEndpoint && clientId && clientSecret) {
            const config = {
                apiEndpoint,
                clientId,
                clientSecret,
                ...(cloudType && { cloudType: "GovCloud" }),
            };
    
            const { promise, cancel } = ToolsIntegrationsAPI.getWizProjects({
                config, ...params
            });
    
            promise.then((response) => {
                if (!_.isEmpty(response.projects)) {
                    setOptionsFromPagination(response.projects);
                }
            });
    
            return { promise, cancel };
        }
    }, [fieldValues, setOptionsFromPagination]);

    const apiEndpointFields = useMemo(() => (
        [{
            name: "apiEndpoint",
            label: "API Endpoint",
            required: true,
            rules:[urlRegexValidator],
            props: {
                onChange: (e) => {
                    const value = e.target.value;
                    form.validateFields(['apiEndpoint'])
                        .then(() => {setIsApiEndpointValid(true)})
                        .catch(() => setIsApiEndpointValid(false))
                        .finally(() => setFieldValues((prev) => ({...prev, apiEndpoint: value})));
                }
            }
        }, {
            name: "clientId",
            label: "Client ID",
            required: true,
            type: FieldTypes.PASSWORD,
            props: {
                onChange: (e) => setFieldValues((prev) => ({...prev, clientId: e.target.value}))
            }
        },
        {
            name: "clientSecret",
            label: "Client Secret",
            required: true,
            type: FieldTypes.PASSWORD,
            props: {
                onChange: (e) => setFieldValues((prev) => ({...prev, clientSecret: e.target.value}))
            }
        },
        {
            name: "cloudType",
            label: "GovCloud",
            inline: true,
            type: FieldTypes.SWITCH,
            help: 'Select only if you are adding GovCloud integration',
            props: {
                leftInlineLabel: true,
            },
        },
        ...(featureFlags.RBVM_ASSETS && type === TYPE.apiEndpoint
            ? [
                {
                    render:() => <Divider type="horizontal" className="m-y-m"/>
                },
                {
                    name: "fetchAssets",
                    label: (
                        <>
                            Fetch Assets{" "}
                            <Tooltip title="Please ensure (read.resources) application permissions is associated with the client secret used for this integration.">
                                <InfoIcon className={'m-l-xs'} />
                            </Tooltip>
                        </>
                    ),
                    type: FieldTypes.BOOLEAN,
                    disabled: !fieldValues.clientSecret,
                    inline: true,
                    props: {
                    checked: !!fieldValues.fetchAssets,
                    onChange: (e) =>
                        setFieldValues((prev) => ({
                            ...prev,
                            fetchAssets: e?.target?.checked,
                        })),
                    },
                },
                {
                    name: "assetProjects",
                    type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
                    options: projects,
                    placeholder: "Leave Blank to Select All Projects",
                    label: "Project Name",
                    disabled: !(isApiEndpointValid && fieldValues.apiEndpoint && fieldValues.clientId && fieldValues.clientSecret && fieldValues.fetchAssets),
                    required: false,
                    props:{
                        api: isApiEndpointValid && fieldValues.apiEndpoint && fieldValues.clientId && fieldValues.clientSecret && fieldValues.fetchAssets ? fetchProjects : undefined,
                        mode: "multiple",
                    }
                  },
                  {
                    name: "assetAssets",
                    type: FieldTypes.MULTI_SELECT,
                    options: wizRBVMAssetTypes,
                    placeholder: "Leave Blank to Select All Assets",
                    label: "Select Asset Types to Ingest",
                    disabled: !(isApiEndpointValid && fieldValues.apiEndpoint && fieldValues.clientId && fieldValues.clientSecret && fieldValues.fetchAssets),
                    required: false,
                  }
              ] : [])
    ]
    ),[featureFlags.RBVM_ASSETS, type, fieldValues.clientSecret, fieldValues.fetchAssets, fieldValues.apiEndpoint, fieldValues.clientId, projects, isApiEndpointValid, fetchProjects, form])

    const wizBucketFields = useMemo(() => ([
        ...apiEndpointFields,
        {
            render:() => <Divider type="horizontal" className="m-y-m"/>
        },
        {
            name: "bucketName",
            label: "S3 Bucket Name",
            required: true,
        },
        {
            name: "region",
            label: "Region",
            required: true,
        },{
            name: "roleArn",
            label: "Role",
            required: true,
        },{
            name: "externalId",
            label: "External Id",
            required: true,
            type: FieldTypes.PASSWORD,
        },{
            name: "folderPath",
            label: "Folder Path",
            required: true,
            props: {
                defaultValue: "Wiz/"
            }
        },
    ]),[apiEndpointFields]);

    const configFields = type === TYPE.apiEndpoint ?  apiEndpointFields : wizBucketFields;

    const fields = useMemo(() => ([
        {
            name: "authenticationType",
            type: FieldTypes.RADIO,
            options: [
                {
                    label: 'Bucket',
                    value: TYPE.wizBucket
                },
                {
                    label: 'API Endpoint',
                    value: TYPE.apiEndpoint
                }
            ],
            props: {
                onChange: handleRadioChange,
                defaultValue: type
            }
        },
        ...configFields,
    ]), [handleRadioChange, type, configFields]);

    const beforeSave = useCallback((data) => {
        const { assetProjects, assetAssets, fetchAssets = false } = data;
        let projectData = {}
        if(assetProjects){
            projectData = projects.reduce((acc,cv) =>{
                const { id, name } = cv;
                if(assetProjects.includes(id)){
                    acc.id.push(id);
                    acc.name.push(name);
                }
                return acc;
            },{id:[],name:[]});
        }
        return {
            ...data,
            assetAssets: _.isEmpty(assetAssets) ? null : assetAssets.join(),
            assetProjects: _.isEmpty(projectData) || _.isEmpty(projectData.id) ? null : projectData.id.join(),
            assetProjectNames: _.isEmpty(projectData) || _.isEmpty(projectData.name) ? null : projectData.name.join(),
            fetchAssets: type === TYPE.apiEndpoint && featureFlags.RBVM_ASSETS && fetchAssets,
            ...(data.cloudType && { cloudType : 'GovCloud'})
        }
    },[featureFlags.RBVM_ASSETS, projects, type]);

    const parseExtraFields = useCallback((config) => {
        return {
            ...config,
            folderPath: _.isEmpty(config.folderPath) ? "Wiz/" : config.folderPath,
            fetchAssets: config.fetchAssets === "true" ? true : false,
            assetAssets: _.isEmpty(config.assetAssets) ? null : config.assetAssets.split(","),
            assetProjects: _.isEmpty(config.assetProjects) ? null : config.assetProjects.split(","),
            assetProjectNames: _.isEmpty(config.assetProjectNames) ? null : config.assetProjectNames.split(","),
            cloudType : config.cloudType === "GovCloud" ? config.cloudType : undefined
        };
    }, [])
    return (
        <GenericPullCredentialsForm
            {...props}
            ref={ref}
            fields={fields}
            MappingForm={WizMappingForm}
            form={form}
            beforeSave={beforeSave}
            initialValue={{ apiEndpoint: 'https://api.{region}.app.wiz.io/graphql', folderPath : "Wiz/" }}
            parseExtraFields = {parseExtraFields}
        />
    )
}

export default forwardRef(WizCredentialsForm);